import { QueryBuilderConfigEntry, UnitFormat } from '@kentik/ui-shared/dictionary/types';

// Note that this is only available via countColumnsSet or dictionary
const countColumns: string[] = [
  // this list contains columns that are not aggregated as /sec metrics
  // new style count distincts
  'f_hll(inet_src_addr,0.0001)',
  'f_hll(inet_dst_addr,0.0001)',
  'f_hll(inet_src_route_prefix,0.0001)',
  'f_hll(inet_dst_route_prefix,0.0001)',
  'f_hll(l4_src_port.agg0,0.0001)',
  'f_hll(l4_dst_port.agg0,0.0001)',
  'f_hll(src_as.agg0,0.0001)',
  'f_hll(dst_as.agg0,0.0001)',
  'f_hll(dst_nexthop_as.agg0,0.0001)',
  'f_hll(src_geo.agg0,0.0001)',
  'f_hll(dst_geo.agg0,0.0001)',
  'f_hll(src_geo_region.agg0,0.0001)',
  'f_hll(dst_geo_region.agg0,0.0001)',
  'f_hll(src_geo_city.agg0,0.0001)',
  'f_hll(dst_geo_city.agg0,0.0001)',
  // legacy count distincts
  'f_countdistinct_inet_src_addr',
  'f_countdistinct_inet_dst_addr',
  'f_countdistinct_inet_src_route_prefix',
  'f_countdistinct_inet_dst_route_prefix',
  'f_countdistinct_src_as',
  'f_countdistinct_dst_as',
  'f_countdistinct_dst_nexthop_as',
  // end legacy
  'perc_retransmits',
  'perc_retransmits_in',
  'perc_retransmits_out',
  'perc_ooorder_in',
  'perc_ooorder_out',
  'perc_fragments',
  'perc_repeated_retransmits',
  'perc_zero_windows',
  'f_sum_zero_windows',
  'f_max_sample_rate',
  'f_avg_sample_rate',
  'kt_intell_order',
  'loss_pct'
];

export const realtimeOffsetSeconds = 8;

export const liveUpdateRefreshWindowSeconds = 10 * 60;

export const reAggDurations = {
  hour: 3600,
  day: 3600 * 24,
  week: 3600 * 24 * 7,
  month: 3600 * 24 * 30
};

export const countColumnsSet = new Set(countColumns);

export const countRegex = [
  '^f_hll.*',
  '^(?!(f_fsumloc|f_fumlat)).*_latency',
  '^(?!(f_fsumloc|f_fumlat)).*_receive_window'
];

export const averageByCountColumns = [
  'f_max_sample_rate',
  'f_avg_sample_rate',
  'f_hll(inet_src_addr,0.0001)',
  'f_hll(inet_dst_addr,0.0001)',
  'f_hll(inet_src_route_prefix,0.0001)',
  'f_hll(inet_dst_route_prefix,0.0001)',
  'f_hll(l4_src_port.agg0,0.0001)',
  'f_hll(l4_dst_port.agg0,0.0001)',
  'f_hll(src_as.agg0,0.0001)',
  'f_hll(dst_as.agg0,0.0001)',
  'f_hll(dst_nexthop_as.agg0,0.0001)',
  'f_hll(src_geo.agg0,0.0001)',
  'f_hll(dst_geo.agg0,0.0001)',
  'f_hll(src_geo_region.agg0,0.0001)',
  'f_hll(dst_geo_region.agg0,0.0001)',
  'f_hll(src_geo_city.agg0,0.0001)',
  'f_hll(dst_geo_city.agg0,0.0001)'
];

// setup how we want to format units, set thresholds for cutting rows (<)
// (1) if sample def, execs val * sample
// (2) if math def, execs Math[math](val)
// (3) do val.toFixed(fixDecimals); if fixDecimals undef, uses 0 (0 is the same as Math.round)
// (4) cut at val < threshold; if threshold undef, uses 1
export const unitFormats: Record<string, UnitFormat> = {
  bytes: {
    // sample: 1/1000000,
    fixDecimals: 2,
    threshold: 0.000001
  },
  in_bytes: {
    // sample: 1/1000000,
    fixDecimals: 2,
    threshold: 0.000001
  },
  out_bytes: {
    // sample: 1/1000000,
    fixDecimals: 2,
    threshold: 0.000001
  },
  packets: {
    // sample: 1/1000000,
    fixDecimals: 2,
    threshold: 0.000000001
  },
  in_packets: {
    // sample: 1/1000000,
    fixDecimals: 2,
    threshold: 0.000000001
  },
  out_packets: {
    // sample: 1/1000000,
    fixDecimals: 2,
    threshold: 0.000000001
  },
  retransmits_in: {
    fixDecimals: 3
    // ,threshold: 0.001
  },
  perc_retransmits_in: {
    fixDecimals: 3,
    threshold: 0.0001
  },
  retransmits_out: {
    fixDecimals: 3
    // ,threshold: 0.001
  },
  perc_retransmits_out: {
    fixDecimals: 3,
    threshold: 0.0001
  },
  repeated_retransmits: {
    fixDecimals: 3
    // ,threshold: 0.001
  },
  perc_repeated_retransmits: {
    fixDecimals: 3,
    threshold: 0.0001
  },
  out_of_order_in: {
    fixDecimals: 3
    // ,threshold: 0.001
  },
  perc_out_of_order_in: {
    fixDecimals: 3,
    threshold: 0.0001
  },
  out_of_order_out: {
    fixDecimals: 3
    // ,threshold: 0.001
  },
  perc_out_of_order_out: {
    fixDecimals: 3,
    threshold: 0.0001
  },
  fragments: {
    fixDecimals: 3
    // ,threshold: 0.001
  },
  perc_fragments: {
    fixDecimals: 3,
    threshold: 0.0001
  },
  zero_windows: {
    fixDecimals: 3
  },
  perc_zero_windows: {
    fixDecimals: 3,
    threshold: 0.0001
  },
  client_latency: {
    fixDecimals: 6,
    threshold: -1
  },
  server_latency: {
    fixDecimals: 6,
    threshold: -1
  },
  appl_latency: {
    fixDecimals: 6,
    threshold: -1
  },
  flex_latency: {
    fixDecimals: 6,
    threshold: -1
  },
  receive_window: {
    math: 'ceil'
  },
  fps: {
    fixDecimals: 3
  },
  max_sample_rate: {
    sample: 1 / 100,
    fixDecimals: 2,
    threshold: -1
  }
};

export const queryBuilderConfig: Record<string, QueryBuilderConfigEntry> = {
  // src
  src_cloud: {
    columns: ['src_cloud'],
    coalesce: ['---'],
    displayName: 'src_cloud'
  },
  src_cloud_service: {
    columns: ['src_cloud_service'],
    coalesce: ['---'],
    displayName: 'src_cloud_service'
  },
  Geography_src: {
    columns: ['src_geo'],
    coalesce: ['---'],
    displayName: 'Geography_src',
    alertingColumns: ['src_geo']
  },
  src_geo_region: {
    columns: ['src_geo_region'],
    coalesce: ['---']
  },
  src_geo_city: {
    columns: ['src_geo_city'],
    coalesce: ['---']
  },
  AS_src: {
    columns: ['i_src_as_name'],
    rawFlowColumns: ['src_as'],
    kflowColumns: ['SRC_AS'],
    displayName: 'i_src_as_name',
    alertingColumns: ['src_as']
  },
  kt_src_as_group: {
    columns: ['kt_src_as_group'],
    coalesce: ['---'],
    displayName: 'i_src_as_name'
  },
  InterfaceID_src: {
    columns: ['i_input_interface_description', 'i_input_snmp_alias'],
    displayConnectors: [' : '],
    coalesce: ['---', '---'],
    rawFlowColumns: ['input_port'],
    kflowColumns: ['INPUT_PORT'],
    displayName: 'input_port',
    alertingColumns: ['input_port']
  },
  src_snmp_id: {
    columns: ['input_port'],
    displayName: 'input_port'
  },
  src_snmp_alias: {
    columns: ['i_input_snmp_alias'],
    displayName: 'i_input_snmp_alias'
  },
  i_input_interface_speed: {
    columns: ['i_input_interface_speed']
  },
  Port_src: { columns: ['l4_src_port'], displayName: 'l4_src_port' },
  src_eth_mac: { columns: ['src_eth_mac'] },
  VLAN_src: { columns: ['vlan_in'], displayName: 'vlan_in' },
  IP_src: {
    columns: ['inet_src_addr'],
    cidr: true,
    displayName: 'inet_src_addr'
  },
  src_route_prefix_len: {
    columns: ['i_src_bgp_prefix'],
    displayName: 'src_route_prefix_len',
    realias: true,
    alertingColumns: ['i_src_bgp_prefix']
  },
  src_route_length: { columns: ['src_route_length'] },
  src_bgp_community: {
    columns: ['src_bgp_community'],
    coalesce: ['---']
  },
  src_bgp_ext_community: {
    columns: ['src_bgp_ext_community'],
    coalesce: ['---']
  },
  src_bgp_large_community: {
    columns: ['src_bgp_large_community'],
    coalesce: ['---']
  },
  src_bgp_origin: {
    columns: ['src_bgp_origin'],
    coalesce: ['---']
  },
  src_bgp_aspath: {
    columns: ['src_bgp_aspath'],
    coalesce: ['---']
  },
  src_aspath_noprepend: {
    columns: ['src_aspath_noprepend'],
    coalesce: ['---']
  },
  src_nexthop_ip: {
    columns: ['inet_src_next_hop'],
    cidr: true,
    displayName: 'inet_src_next_hop'
  },
  src_nexthop_asn: {
    columns: ['i_src_nexthop_as_name'],
    displayName: 'i_src_nexthop_as_name',
    alertingColumns: ['src_nexthop_as']
  },
  src_second_asn: {
    columns: ['i_src_second_asn_name'],
    displayName: 'i_src_second_asn_name',
    alertingColumns: ['src_second_asn']
  },
  src_third_asn: {
    columns: ['i_src_third_asn_name'],
    displayName: 'i_src_third_asn_name',
    alertingColumns: ['src_third_asn']
  },
  src_secondhop_asn_noprepend: {
    columns: ['i_src_secondhop_asn_noprepend_name'],
    displayName: 'i_src_secondhop_asn_noprepend_name',
    alertingColumns: ['src_secondhop_asn_noprepend_name']
  },
  src_thirdhop_asn_noprepend: {
    columns: ['i_src_thirdhop_asn_noprepend_name'],
    displayName: 'i_src_thirdhop_asn_noprepend_name',
    alertingColumns: ['src_thirdhop_asn_noprepend_name']
  },
  src_asn_generic: {
    columns: ['i_src_asn_generic_name'],
    displayName: 'i_src_asn_generic_name',
    alertingColumns: ['src_asn_generic']
  },
  src_proto_port: {
    columns: ['i_l4_src_port_name'],
    displayName: 'i_l4_src_port_name'
  },
  i_src_connect_type_name: {
    columns: ['i_src_connect_type_name'],
    coalesce: ['---'],
    displayName: 'i_src_connect_type_name'
  },
  i_src_network_bndry_name: {
    columns: ['i_src_network_bndry_name'],
    coalesce: ['---'],
    displayName: 'i_src_network_bndry_name'
  },
  src_threat_bnetcc: {
    columns: ['src_threat_bnetcc'],
    coalesce: ['---'],
    displayName: 'src_threat_bnetcc'
  },
  src_threat_host: {
    columns: ['src_threat_host'],
    coalesce: ['---'],
    displayName: 'src_threat_host'
  },
  src_cdn: {
    columns: ['i_src_cdn'],
    coalesce: ['---'],
    displayName: 'src_cdn'
  },
  i_trf_origination: {
    columns: ['i_trf_origination'],
    coalesce: ['---'],
    displayName: 'i_trf_origination'
  },
  i_src_provider_classification: {
    columns: ['i_src_provider_classification'],
    coalesce: ['---'],
    displayName: 'i_src_provider_classification'
  },
  input_vrf: {
    columns: ['input_vrf'],
    displayName: 'input_vrf'
  },
  i_input_vrf: {
    columns: ['i_input_vrf'],
    coalesce: ['---'],
    displayName: 'i_input_vrf'
  },
  i_input_vrf_rd: {
    columns: ['i_input_vrf_rd'],
    coalesce: ['---'],
    displayName: 'i_input_vrf_rd'
  },
  i_input_vrf_rt: {
    columns: ['i_input_vrf_rt'],
    coalesce: ['---'],
    displayName: 'i_input_vrf_rt'
  },

  // dst
  dst_cloud: {
    columns: ['dst_cloud'],
    coalesce: ['---'],
    displayName: 'dst_cloud'
  },
  dst_cloud_service: {
    columns: ['dst_cloud_service'],
    coalesce: ['---'],
    displayName: 'dst_cloud_service'
  },
  Geography_dst: {
    columns: ['dst_geo'],
    coalesce: ['---'],
    displayName: 'Geography_dst',
    alertingColumns: ['dst_geo']
  },
  dst_geo_region: {
    columns: ['dst_geo_region'],
    coalesce: ['---']
  },
  dst_geo_city: {
    columns: ['dst_geo_city'],
    coalesce: ['---']
  },
  AS_dst: {
    columns: ['i_dst_as_name'],
    rawFlowColumns: ['dst_as'],
    kflowColumns: ['DST_AS'],
    displayName: 'i_dst_as_name',
    alertingColumns: ['dst_as']
  },
  kt_dst_as_group: {
    columns: ['kt_dst_as_group'],
    coalesce: ['---'],
    displayName: 'i_dst_as_name'
  },
  InterfaceID_dst: {
    columns: ['i_output_interface_description', 'i_output_snmp_alias'],
    displayConnectors: [' : '],
    coalesce: ['---', '---'],
    rawFlowColumns: ['output_port'],
    kflowColumns: ['OUTPUT_PORT'],
    displayName: 'output_port',
    alertingColumns: ['output_port']
  },
  dst_snmp_id: {
    columns: ['output_port'],
    displayName: 'output_port'
  },
  i_output_interface_speed: {
    columns: ['i_output_interface_speed']
  },
  Port_dst: { columns: ['l4_dst_port'], displayName: 'l4_dst_port' },
  dst_eth_mac: { columns: ['dst_eth_mac'] },
  VLAN_dst: { columns: ['vlan_out'], displayName: 'vlan_out' },
  IP_dst: {
    columns: ['inet_dst_addr'],
    cidr: true,
    displayName: 'inet_dst_addr'
  },
  dst_route_prefix_len: {
    columns: ['i_dst_bgp_prefix'],
    displayName: 'dst_route_prefix_len',
    realias: true,
    alertingColumns: ['i_dst_bgp_prefix']
  },
  dst_route_length: { columns: ['dst_route_length'] },
  dst_bgp_community: {
    columns: ['dst_bgp_community'],
    coalesce: ['---']
  },
  dst_bgp_ext_community: {
    columns: ['dst_bgp_ext_community'],
    coalesce: ['---']
  },
  dst_bgp_large_community: {
    columns: ['dst_bgp_large_community'],
    coalesce: ['---']
  },
  dst_bgp_origin: {
    columns: ['dst_bgp_origin'],
    coalesce: ['---']
  },
  dst_bgp_aspath: {
    columns: ['dst_bgp_aspath'],
    coalesce: ['---'],
    allowsCut: true
  },
  dst_aspath_noprepend: {
    columns: ['dst_aspath_noprepend'],
    coalesce: ['---'],
    allowsCut: true
  },
  dst_nexthop_ip: {
    columns: ['inet_dst_next_hop'],
    cidr: true,
    displayName: 'inet_dst_next_hop'
  },
  dst_nexthop_asn: {
    columns: ['i_dst_nexthop_as_name'],
    displayName: 'i_dst_nexthop_as_name',
    alertingColumns: ['dst_nexthop_as']
  },
  dst_second_asn: {
    columns: ['i_dst_second_asn_name'],
    displayName: 'i_dst_second_asn_name',
    alertingColumns: ['dst_second_asn']
  },
  dst_third_asn: {
    columns: ['i_dst_third_asn_name'],
    displayName: 'i_dst_third_asn_name',
    alertingColumns: ['dst_third_asn']
  },
  dst_secondhop_asn_noprepend: {
    columns: ['i_dst_secondhop_asn_noprepend_name'],
    displayName: 'i_dst_secondhop_asn_noprepend_name',
    alertingColumns: ['dst_secondhop_asn_noprepend_name']
  },
  dst_thirdhop_asn_noprepend: {
    columns: ['i_dst_thirdhop_asn_noprepend_name'],
    displayName: 'i_dst_thirdhop_asn_noprepend_name',
    alertingColumns: ['dst_thirdhop_asn_noprepend_name']
  },
  dst_asn_generic: {
    columns: ['i_dst_asn_generic_name'],
    displayName: 'i_dst_asn_generic_name',
    alertingColumns: ['dst_asn_generic']
  },
  i_ult_exit_site: {
    columns: ['i_ult_exit_site'],
    coalesce: ['Unassigned']
  },
  i_ult_exit_site_country: {
    columns: ['i_ult_exit_site_country'],
    coalesce: ['Unassigned']
  },
  i_ult_exit_site_market: {
    columns: ['i_ult_exit_site_market'],
    coalesce: ['Unassigned']
  },
  i_ult_exit_device_name: {
    columns: ['i_ult_exit_device_name']
  },
  ult_exit_device_id: {
    columns: ['ult_exit_device_id'],
    displayName: 'ult_exit_device_id'
  },
  ult_exit_port: {
    columns: ['ult_exit_port'],
    displayName: 'ult_exit_port'
  },
  i_ult_exit_snmp_alias: {
    columns: ['i_ult_exit_snmp_alias'],
    displayName: 'i_ult_exit_snmp_alias'
  },
  bgp_ult_exit_interface: {
    columns: ['i_ult_exit_interface_description', 'i_ult_exit_snmp_alias'],
    displayConnectors: [' : '],
    coalesce: ['---', '---'],
    displayName: 'bgp_ult_exit_interface'
  },
  i_ult_exit_connect_type_name: {
    columns: ['i_ult_exit_connect_type_name'],
    coalesce: ['---'],
    displayName: 'i_ult_exit_connect_type_name'
  },
  i_ult_exit_network_bndry_name: {
    columns: ['i_ult_exit_network_bndry_name'],
    coalesce: ['---'],
    displayName: 'i_ult_exit_network_bndry_name'
  },
  i_ult_provider_classification: {
    columns: ['i_ult_provider_classification'],
    coalesce: ['---'],
    displayName: 'i_ult_provider_classification'
  },
  i_ult_exit_vrf: {
    columns: ['i_ult_exit_vrf'],
    displayName: 'i_ult_exit_vrf'
  },
  i_ult_exit_vrf_rd: {
    columns: ['i_ult_exit_vrf_rd'],
    displayName: 'i_ult_exit_vrf_rd'
  },
  dst_proto_port: {
    columns: ['i_l4_dst_port_name'],
    displayName: 'i_l4_dst_port_name'
  },
  i_dst_connect_type_name: {
    columns: ['i_dst_connect_type_name'],
    coalesce: ['---'],
    displayName: 'i_dst_connect_type_name'
  },
  i_dst_network_bndry_name: {
    columns: ['i_dst_network_bndry_name'],
    coalesce: ['---'],
    displayName: 'i_dst_network_bndry_name'
  },
  dst_threat_bnetcc: {
    columns: ['dst_threat_bnetcc'],
    coalesce: ['---'],
    displayName: 'dst_threat_bnetcc'
  },
  dst_threat_host: {
    columns: ['dst_threat_host'],
    coalesce: ['---'],
    displayName: 'dst_threat_host'
  },
  dst_cdn: {
    columns: ['i_dst_cdn'],
    coalesce: ['---'],
    displayName: 'dst_cdn'
  },
  i_trf_termination: {
    columns: ['i_trf_termination'],
    coalesce: ['---'],
    displayName: 'i_trf_termination'
  },
  i_dst_provider_classification: {
    columns: ['i_dst_provider_classification'],
    coalesce: ['---'],
    displayName: 'i_dst_provider_classification'
  },
  output_vrf: {
    columns: ['output_vrf'],
    displayName: 'output_vrf'
  },
  i_output_vrf: {
    columns: ['i_output_vrf'],
    coalesce: ['---'],
    displayName: 'i_output_vrf'
  },
  i_output_vrf_rd: {
    columns: ['i_output_vrf_rd'],
    coalesce: ['---'],
    displayName: 'i_output_vrf_rd'
  },
  i_output_vrf_rt: {
    columns: ['i_output_vrf_rt'],
    coalesce: ['---'],
    displayName: 'i_output_vrf_rt'
  },
  i_dst_rpki_name: {
    columns: ['i_dst_rpki_name'],
    coalesce: ['---'],
    displayName: 'i_dst_rpki_name'
  },
  i_dst_rpki_min_name: {
    columns: ['i_dst_rpki_min_name'],
    coalesce: ['---'],
    displayName: 'i_dst_rpki_min_name'
  },
  dscp: {
    columns: ['dscp'],
    displayName: 'dscp'
  },
  ecn: {
    columns: ['ecn'],
    displayName: 'ecn'
  },
  // total
  TopFlow: {
    columns: ['protocol', 'inet_src_addr', 'l4_src_port', 'inet_dst_addr', 'l4_dst_port'],
    displayConnectors: ['-', ':', '->', ':'],
    displayName: 'flow'
  },
  Traffic: {
    columns: ["'Total'"],
    alertingColumns: ["'TOTAL'"],
    displayName: 'name'
  },
  Proto: {
    columns: ['i_protocol_name'],
    rawFlowColumns: ['protocol'],
    kflowColumns: ['PROTOCOL'],
    alertingColumns: ['protocol'],
    displayName: 'i_protocol_name'
  },
  InterfaceTopTalkers: {
    columns: [
      'i_input_interface_description',
      'i_input_snmp_alias',
      'input_port',
      'i_output_interface_description',
      'i_output_snmp_alias',
      'output_port'
    ],
    displayConnectors: [' : ', ' (', ') -> ', ' : ', ' (', ') '],
    coalesce: ['---', '---', null, '---', '---', null],
    displayName: 'input_port_all'
  },
  PortPortTalkers: {
    columns: ['protocol', 'l4_src_port', 'l4_dst_port'],
    displayConnectors: ['-', '->'],
    displayName: 'PortPortTalkers'
  },
  RegionTopTalkers: {
    columns: ['src_geo_region', 'dst_geo_region'],
    displayConnectors: ['->'],
    displayName: 'RegionTopTalkers'
  },
  TopFlowsIP: {
    columns: ['inet_src_addr', 'inet_dst_addr'],
    displayConnectors: ['->'],
    cidr: true,
    postConnect: true,
    displayName: 'topflowsip'
  },
  ASTopTalkers: {
    columns: ['i_src_as_name', 'src_as', 'i_dst_as_name', 'dst_as'],
    displayConnectors: [' (', ')->', ' (', ')'],
    coalesce: ['---', null, '---', null],
    displayName: 'ASTopTalkers'
  },
  inet_family: { columns: ['inet_family'] },
  ipv6_flow_label: {
    columns: ['ipv6_flow_label'],
    convertHex: true,
    displayName: 'ipv6_flow_label'
  },
  TOS: { columns: ['tos'] },
  tcp_flags: {
    columns: ['i_tcp_flag_names'],
    displayName: 'i_tcp_flag_names',
    alertingColumns: ['tcp_flags']
  },
  application: {
    columns: ['application'],
    coalesce: ['---']
  },
  i_device_id: {
    columns: ['i_device_name'],
    rawFlowColumns: ['i_device_name'],
    kflowColumns: ['device_id'],
    displayName: 'i_device_name',
    alertingColumns: ['i_device_id']
  },
  device_id: {
    columns: ['i_device_id'],
    displayName: 'i_device_id'
  },
  i_device_site_name: {
    columns: ['i_device_site_name'],
    coalesce: ['Unassigned'],
    displayName: 'i_device_site_name'
  },
  i_device_site_country: {
    columns: ['i_device_site_country'],
    coalesce: ['Unassigned']
  },
  i_site_market: {
    columns: ['i_site_market'],
    coalesce: ['Unassigned']
  },
  sampledpktsize: {
    columns: ['sampledpktsize']
  },
  sampledpktsize_100: {
    columns: ['sampledpktsize_100']
  },
  sample_rate: {
    columns: ['sample_rate']
  },
  i_trf_profile: {
    columns: ['i_trf_profile'],
    displayName: 'i_trf_profile'
  },
  simple_trf_prof: {
    columns: ['simple_trf_prof'],
    displayName: 'simple_trf_prof'
  },
  i_host_direction: {
    columns: ['i_host_direction'],
    displayName: 'i_host_direction'
  },
  device_sample_rate: {
    columns: ['device_sample_rate'],
    displayName: 'device_sample_rate'
  },
  connection_id: {
    columns: ['connection_id'],
    displayName: 'connection_id'
  },
  service_name: {
    columns: ['service_name'],
    coalesce: ['---'],
    displayName: 'service_name',
    allowsCut: true
  },
  service_type: {
    columns: ['service_type'],
    coalesce: ['---'],
    displayName: 'service_type'
  },
  service_provider: {
    columns: ['service_provider'],
    coalesce: ['---'],
    displayName: 'service_provider'
  },
  ott_classification_type: {
    columns: ['ott_classification_type'],
    displayName: 'ott_classification_type'
  },
  ktrac_prefix_len: {
    columns: ['inet_01', 'int05'],
    displayConnectors: ['/'],
    displayName: 'ktrac_prefix_len'
  },

  // units
  bytes: {
    columns: ['sum(both_bytes) as f_sum_both_bytes'],
    windowBy: ['sum(both_bytes)'],
    useOrderByFilter: true,
    orderByFilter: ['both_bytes'],
    orderBy: ['f_sum_both_bytes']
  },
  in_bytes: {
    columns: ['sum(in_bytes) as f_sum_in_bytes'],
    windowBy: ['sum(in_bytes)'],
    useOrderByFilter: true,
    orderByFilter: ['in_bytes'],
    orderBy: ['f_sum_in_bytes']
  },
  out_bytes: {
    columns: ['sum(out_bytes) as f_sum_out_bytes'],
    windowBy: ['sum(out_bytes)'],
    useOrderByFilter: true,
    orderByFilter: ['out_bytes'],
    orderBy: ['f_sum_out_bytes']
  },
  packets: {
    columns: ['sum(both_pkts) AS f_sum_both_pkts'],
    windowBy: ['sum(both_pkts)'],
    useOrderByFilter: true,
    orderByFilter: ['both_pkts'],
    orderBy: ['f_sum_both_pkts']
  },
  in_packets: {
    columns: ['sum(in_pkts) as f_sum_in_pkts'],
    windowBy: ['sum(in_pkts)'],
    useOrderByFilter: true,
    orderByFilter: ['in_pkts'],
    orderBy: ['f_sum_in_pkts']
  },
  out_packets: {
    columns: ['sum(out_pkts) as f_sum_out_pkts'],
    windowBy: ['sum(out_pkts)'],
    useOrderByFilter: true,
    orderByFilter: ['out_pkts'],
    orderBy: ['f_sum_out_pkts']
  },
  fps: {
    columns: ['$_ktfn$'],
    ktFns: {
      outsort: 'sum(i_count) AS trautocount',
      divisor: 'sum(agg_int0) AS "f_fumlat_$_numerator$^0,100,1,1000000"',
      logsum: 'sum(agg_int0) AS "f_fsumloc_$_numerator$^0,100,1,1000000"'
    },
    windowBy: ['sum(i_count)'],
    orderBy: ['trautocount']
  },
  unique_src_ip: {
    columns: ['kt_hll_cardinality(inet_src_addr) as "f_hll(inet_src_addr,0.0001)"'],
    windowBy: ['kt_hll_cardinality(inet_src_addr)'],
    orderBy: ['"f_hll(inet_src_addr,0.0001)"']
  },
  unique_dst_ip: {
    columns: ['kt_hll_cardinality(inet_dst_addr) as "f_hll(inet_dst_addr,0.0001)"'],
    windowBy: ['kt_hll_cardinality(inet_dst_addr)'],
    orderBy: ['"f_hll(inet_dst_addr,0.0001)"']
  },
  unique_src_route_prefix: {
    columns: ['kt_hll_cardinality(inet_src_route_prefix) as "f_hll(inet_src_route_prefix,0.0001)"'],
    windowBy: ['kt_hll_cardinality(inet_src_route_prefix)'],
    orderBy: ['"f_hll(inet_src_route_prefix,0.0001)"']
  },
  unique_dst_route_prefix: {
    columns: ['kt_hll_cardinality(inet_dst_route_prefix) as "f_hll(inet_dst_route_prefix,0.0001)"'],
    windowBy: ['kt_hll_cardinality(inet_dst_route_prefix)'],
    orderBy: ['"f_hll(inet_dst_route_prefix,0.0001)"']
  },
  unique_src_port: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(l4_src_port.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(l4_src_port.agg0,0.0001)"'],
    isGenericAgg: true
  },
  unique_dst_port: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(l4_dst_port.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(l4_dst_port.agg0,0.0001)"'],
    isGenericAgg: true
  },
  unique_src_as: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(src_as.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(src_as.agg0,0.0001)"'],
    isGenericAgg: true
  },
  unique_dst_as: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(dst_as.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(dst_as.agg0,0.0001)"'],
    isGenericAgg: true
  },
  unique_dst_nexthop_asn: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(dst_nexthop_as.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(dst_nexthop_as.agg0,0.0001)"'],
    isGenericAgg: true
  },
  unique_src_geo: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(src_geo.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(src_geo.agg0,0.0001)"'],
    isGenericAgg: true
  },
  unique_dst_geo: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(dst_geo.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(dst_geo.agg0,0.0001)"'],
    isGenericAgg: true
  },
  unique_src_geo_region: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(src_geo_region.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(src_geo_region.agg0,0.0001)"'],
    isGenericAgg: true
  },
  unique_dst_geo_region: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(dst_geo_region.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(dst_geo_region.agg0,0.0001)"'],
    isGenericAgg: true
  },
  unique_src_geo_city: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(src_geo_city.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(src_geo_city.agg0,0.0001)"'],
    isGenericAgg: true
  },
  unique_dst_geo_city: {
    columns: ['kt_hll_cardinality(agg0) as "f_hll(dst_geo_city.agg0,0.0001)"'],
    windowBy: ['kt_hll_cardinality(agg0)'],
    orderBy: ['"f_hll(dst_geo_city.agg0,0.0001)"'],
    isGenericAgg: true
  },
  // Synth
  probes_lost: {
    columns: ['sum(INT13) as f_sum_int13'],
    windowBy: ['sum(INT13)'],
    orderBy: ['f_sum_int13']
  },
  probes_sent: {
    columns: ['sum(INT12) as f_sum_int12'],
    windowBy: ['sum(INT12)'],
    orderBy: ['f_sum_int12']
  },
  loss_pct: {
    columns: ['(sum(INT13)/(CASE sum(INT12) WHEN 0 THEN 1 ELSE sum(INT12) END))*100 as loss_pct'],
    ratioBased: true,
    numeratorUnit: 'probes_lost',
    denominatorUnit: 'probes_sent',
    windowBy: ['(sum(INT13)/(CASE sum(INT12) WHEN 0 THEN 1 ELSE sum(INT12) END))*100'],
    orderBy: ['loss_pct']
  },
  // OTT
  bytes_per_src_ip: {
    columns: [
      'sum(both_bytes)/(CASE kt_hll_cardinality(inet_src_addr) WHEN 0 THEN 1 ELSE kt_hll_cardinality(inet_src_addr) END) as bytes_per_src_ip'
    ],
    ratioBased: true,
    numeratorUnit: 'unique_src_ip',
    denominatorUnit: 'bytes',
    windowBy: [
      'sum(both_bytes)/(CASE kt_hll_cardinality(inet_src_addr) WHEN 0 THEN 1 ELSE kt_hll_cardinality(inet_src_addr) END)'
    ],
    orderBy: ['bytes_per_src_ip']
  },
  bytes_per_dst_ip: {
    columns: [
      'sum(both_bytes)/(CASE kt_hll_cardinality(inet_dst_addr) WHEN 0 THEN 1 ELSE kt_hll_cardinality(inet_dst_addr) END) as bytes_per_dst_ip'
    ],
    ratioBased: true,
    numeratorUnit: 'unique_dst_ip',
    denominatorUnit: 'bytes',
    windowBy: [
      'sum(both_bytes)/(CASE kt_hll_cardinality(inet_dst_addr) WHEN 0 THEN 1 ELSE kt_hll_cardinality(inet_dst_addr) END)'
    ],
    orderBy: ['bytes_per_dst_ip']
  },
  // nprobe hosts
  retransmits_in: {
    columns: ['sum(retransmitted_in_pkts) AS $_ktfn$_retransmitted_in_pkts'],
    ktFns: {
      outsort: 'f_sum',
      divisor: 'f_frxtmtsb',
      logsum: 'f_fsumlogb'
    },
    windowBy: ['sum(retransmitted_in_pkts)'],
    orderBy: ['f_sum_retransmitted_in_pkts']
  },
  perc_retransmits_in: {
    columns: [
      'sum(retransmitted_in_pkts)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100 as perc_retransmits_in'
    ],
    ratioBased: true,
    numeratorUnit: 'retransmits_in',
    denominatorUnit: 'packets',
    windowBy: ['sum(retransmitted_in_pkts)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100'],
    orderBy: ['perc_retransmits_in']
  },
  retransmits_out: {
    columns: ['sum(retransmitted_out_pkts) AS $_ktfn$_retransmitted_out_pkts'],
    ktFns: {
      outsort: 'f_sum',
      divisor: 'f_frxtmtsb',
      logsum: 'f_fsumlogb'
    },
    windowBy: ['sum(retransmitted_out_pkts)'],
    orderBy: ['f_sum_retransmitted_out_pkts']
  },
  perc_retransmits_out: {
    columns: [
      'sum(retransmitted_out_pkts)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100 as perc_retransmits_out'
    ],
    ratioBased: true,
    numeratorUnit: 'retransmits_out',
    denominatorUnit: 'packets',
    windowBy: ['sum(retransmitted_out_pkts)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100'],
    orderBy: ['perc_retransmits_out']
  },
  repeated_retransmits: {
    columns: ['sum(repeated_retransmits) AS $_ktfn$_repeated_retransmits'],
    ktFns: {
      outsort: 'f_sum',
      divisor: 'f_frxtmtsb',
      logsum: 'f_fsumlogb'
    },
    windowBy: ['sum(repeated_retransmits)'],
    orderBy: ['f_sum_repeated_retransmits']
  },
  perc_repeated_retransmits: {
    columns: [
      'sum(repeated_retransmits)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100 as perc_repeated_retransmits'
    ],
    ratioBased: true,
    numeratorUnit: 'repeated_retransmits',
    denominatorUnit: 'packets',
    windowBy: ['sum(repeated_retransmits)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100'],
    orderBy: ['perc_repeated_retransmits']
  },
  zero_windows: {
    columns: ['sum(zero_windows) AS $_ktfn$_zero_windows'],
    ktFns: {
      outsort: 'f_sum',
      divisor: 'f_frxtmtsb',
      logsum: 'f_fsumlogb'
    },
    windowBy: ['sum(zero_windows)'],
    orderBy: ['f_sum_zero_windows']
  },
  perc_zero_windows: {
    columns: ['sum(zero_windows)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100 as perc_zero_windows'],
    ratioBased: true,
    numeratorUnit: 'zero_windows',
    denominatorUnit: 'packets',
    windowBy: ['sum(zero_windows)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100'],
    orderBy: ['perc_zero_windows']
  },
  out_of_order_in: {
    columns: ['sum(ooorder_in_pkts) AS $_ktfn$_ooorder_in_pkts'],
    ktFns: {
      outsort: 'f_sum',
      divisor: 'f_frxtmtsb',
      logsum: 'f_fsumlogb'
    },
    windowBy: ['sum(ooorder_in_pkts)'],
    orderBy: ['f_sum_ooorder_in_pkts']
  },
  perc_out_of_order_in: {
    columns: [
      'sum(ooorder_in_pkts)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100 as perc_ooorder_in'
    ],
    ratioBased: true,
    numeratorUnit: 'out_of_order_in',
    denominatorUnit: 'packets',
    windowBy: ['sum(ooorder_in_pkts)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100'],
    orderBy: ['perc_ooorder_in']
  },
  out_of_order_out: {
    columns: ['sum(ooorder_out_pkts) AS $_ktfn$_ooorder_out_pkts'],
    ktFns: {
      outsort: 'f_sum',
      divisor: 'f_frxtmtsb',
      logsum: 'f_fsumlogb'
    },
    windowBy: ['sum(ooorder_out_pkts)'],
    orderBy: ['f_sum_ooorder_out_pkts']
  },
  perc_out_of_order_out: {
    columns: [
      'sum(ooorder_out_pkts)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100 as perc_ooorder_out'
    ],
    ratioBased: true,
    numeratorUnit: 'out_of_order_out',
    denominatorUnit: 'packets',
    windowBy: ['sum(ooorder_out_pkts)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100'],
    orderBy: ['perc_ooorder_out']
  },
  fragments: {
    columns: ['sum(fragments) AS $_ktfn$_fragments'],
    ktFns: {
      outsort: 'f_sum',
      divisor: 'f_frxtmtsb',
      logsum: 'f_fsumlogb'
    },
    windowBy: ['sum(fragments)'],
    orderBy: ['f_sum_fragments']
  },
  perc_fragments: {
    columns: ['sum(fragments)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100 as perc_fragments'],
    ratioBased: true,
    numeratorUnit: 'fragments',
    denominatorUnit: 'packets',
    windowBy: ['sum(fragments)/(CASE sum(both_pkts) WHEN 0 THEN 1 ELSE sum(both_pkts) END)*100'],
    orderBy: ['perc_fragments']
  },
  receive_window_sum: {
    columns: ['sum(receive_window) AS $_ktfn$'],
    ktFns: {
      outsort: 'f_sum_receive_window',
      divisor: '"f_percentile_sum(receive_window,$_minpctile$,$_maxpctile$,$_minval$,$_maxval$)"',
      logsum: '"f_percentile_sum(receive_window,$_minpctile$,$_maxpctile$,$_minval$,$_maxval$)"'
    },
    windowBy: ['sum(receive_window)'],
    orderBy: ['f_sum_receive_window']
  },
  receive_window: {
    columns: ['sum(receive_window) / (CASE sum(agg_int0) WHEN 0 THEN 1 ELSE sum(agg_int0) END) AS avg_receive_window'],
    hasLimits: true,
    ratioBased: true,
    numeratorUnit: 'receive_window_sum',
    numeratorColumn: 'receive_window',
    denominatorUnit: 'fps',
    windowBy: ['sum(receive_window) / (CASE sum(agg_int0) WHEN 0 THEN 1 ELSE sum(agg_int0) END)'],
    orderBy: ['avg_receive_window']
  },
  client_latency_sum: {
    columns: ['sum(client_nw_latency_ms) AS $_ktfn$'],
    ktFns: {
      outsort: 'f_sum_client_nw_latency_ms',
      divisor: '"f_percentile_sum(client_nw_latency_ms,$_minpctile$,$_maxpctile$,$_minval$,$_maxval$)"',
      logsum: '"f_percentile_sum(client_nw_latency_ms,$_minpctile$,$_maxpctile$,$_minval$,$_maxval$)"'
    },
    windowBy: ['sum(client_nw_latency_ms)'],
    orderBy: ['f_sum_client_nw_latency_ms']
  },
  client_latency: {
    columns: ['sum(client_nw_latency_ms) / (CASE sum($_ktfn$) WHEN 0 THEN 1 ELSE sum($_ktfn$) END) AS client_latency'],
    ktFns: {
      outsort: 'i_count',
      divisor: 'agg_int0',
      logsum: 'agg_int0'
    },
    hasLimits: true,
    ratioBased: true,
    numeratorUnit: 'client_latency_sum',
    numeratorColumn: 'client_nw_latency_ms',
    denominatorUnit: 'fps',
    windowBy: ['sum(client_nw_latency_ms) / (CASE sum(agg_int0) WHEN 0 THEN 1 ELSE sum(agg_int0) END)'],
    orderBy: ['client_latency']
  },
  server_latency_sum: {
    columns: ['sum(server_nw_latency_ms) AS $_ktfn$'],
    ktFns: {
      outsort: 'f_sum_server_nw_latency_ms',
      divisor: '"f_percentile_sum(server_nw_latency_ms,$_minpctile$,$_maxpctile$,$_minval$,$_maxval$)"',
      logsum: '"f_percentile_sum(server_nw_latency_ms,$_minpctile$,$_maxpctile$,$_minval$,$_maxval$)"'
    },
    windowBy: ['sum(server_nw_latency_ms)'],
    orderBy: ['f_sum_server_nw_latency_ms']
  },
  server_latency: {
    columns: ['sum(server_nw_latency_ms) / (CASE sum($_ktfn$) WHEN 0 THEN 1 ELSE sum($_ktfn$) END) AS server_latency'],
    ktFns: {
      outsort: 'i_count',
      divisor: 'agg_int0',
      logsum: 'agg_int0'
    },
    hasLimits: true,
    ratioBased: true,
    numeratorUnit: 'server_latency_sum',
    numeratorColumn: 'server_nw_latency_ms',
    denominatorUnit: 'fps',
    windowBy: ['sum(server_nw_latency_ms) / (CASE sum(agg_int0) WHEN 0 THEN 1 ELSE sum(agg_int0) END)'],
    orderBy: ['server_latency']
  },
  appl_latency_sum: {
    columns: ['sum(appl_latency_ms) AS $_ktfn$'],
    ktFns: {
      outsort: 'f_sum_appl_latency_ms',
      divisor: '"f_percentile_sum(appl_latency_ms,$_minpctile$,$_maxpctile$,$_minval$,$_maxval$)"',
      logsum: '"f_percentile_sum(appl_latency_ms,$_minpctile$,$_maxpctile$,$_minval$,$_maxval$)"'
    },
    windowBy: ['sum(appl_latency_ms)'],
    orderBy: ['f_sum_appl_latency_ms']
  },
  appl_latency: {
    columns: ['sum(appl_latency_ms) / (CASE sum($_ktfn$) WHEN 0 THEN 1 ELSE sum($_ktfn$) END) AS appl_latency'],
    ktFns: {
      outsort: 'i_count',
      divisor: 'agg_int0',
      logsum: 'agg_int0'
    },
    hasLimits: true,
    ratioBased: true,
    numeratorUnit: 'appl_latency_sum',
    numeratorColumn: 'appl_latency_ms',
    denominatorUnit: 'fps',
    windowBy: ['sum(appl_latency_ms) / (CASE sum(agg_int0) WHEN 0 THEN 1 ELSE sum(agg_int0) END)'],
    orderBy: ['appl_latency']
  },
  fpex_latency_sum: {
    columns: ['sum(fpex_latency_ms) AS $_ktfn$'],
    ktFns: {
      outsort: 'f_sum_fpex_latency_ms',
      divisor: '"f_percentile_sum(fpex_latency_ms,$_minpctile$,$_maxpctile$,$_minval$,$_maxval$)"',
      logsum: '"f_percentile_sum(fpex_latency_ms,$_minpctile$,$_maxpctile$,$_minval$,$_maxval$)"'
    },
    windowBy: ['sum(fpex_latency_ms)'],
    orderBy: ['f_sum_fpex_latency_ms']
  },
  fpex_latency: {
    columns: ['sum(fpex_latency_ms) / (CASE sum($_ktfn$) WHEN 0 THEN 1 ELSE sum($_ktfn$) END) AS fpex_latency'],
    ktFns: {
      outsort: 'i_count',
      divisor: 'agg_int0',
      logsum: 'agg_int0'
    },
    hasLimits: true,
    ratioBased: true,
    numeratorUnit: 'fpex_latency_sum',
    numeratorColumn: 'fpex_latency_ms',
    denominatorUnit: 'fps',
    windowBy: ['sum(fpex_latency_ms) / (CASE sum(agg_int0) WHEN 0 THEN 1 ELSE sum(agg_int0) END)'],
    orderBy: ['fpex_latency']
  },
  max_sample_rate: {
    columns: ['max(sample_rate) AS f_max_sample_rate'],
    windowBy: ['max(sample_rate)'],
    orderBy: ['f_max_sample_rate']
  },
  avg_sample_rate: {
    columns: ['avg(sample_rate) AS f_avg_sample_rate'],
    windowBy: ['avg(sample_rate)'],
    orderBy: ['f_avg_sample_rate']
  },

  // nprobe dns hosts
  kflow_dns_query: {
    columns: ['kflow_dns_query'],
    coalesce: ['---'],
    allowsCut: true
  },
  kflow_dns_query_type: {
    columns: ['i_kflow_dns_query_type_name'],
    displayName: 'i_kflow_dns_query_type_name'
  },
  kflow_dns_ret_code: {
    columns: ['kflow_dns_ret_code']
  },
  kflow_dns_response: {
    columns: ['kflow_dns_response'],
    coalesce: ['---']
  },
  kflow_http_url: {
    columns: ['kflow_http_url'],
    coalesce: ['---'],
    allowsCut: true
  },
  kflow_http_status: {
    columns: ['kflow_http_status']
  },
  kflow_http_referer: {
    columns: ['kflow_http_referer'],
    coalesce: ['---']
  },
  kflow_http_ua: {
    columns: ['kflow_http_ua'],
    coalesce: ['---']
  },
  kflow_http_host: {
    columns: ['kflow_http_host'],
    coalesce: ['---'],
    allowsCut: true
  },

  // logsum
  kt_intell_order: {
    columns: ['sum(agg_int1) as kt_intell_order'],
    windowBy: ['sum(agg_int1)'],
    orderBy: ['kt_intell_order']
  },
  dst_sr_sid: {
    columns: ['dst_sr_sid'],
    displayName: 'dst_sr_sid'
  },
  dst_sr_sidpath: {
    columns: ['dst_sr_sidpath'],
    displayName: 'dst_sr_sidpath'
  },

  // aws
  kt_aws_src_vpc_name: {
    columns: ['kt_aws_src_vpc_id'],
    coalesce: ['---'],
    omitWithoutCoalesce: true,
    displayName: 'kt_aws_src_vpc_name'
  },
  kt_aws_dst_vpc_name: {
    columns: ['kt_aws_dst_vpc_id'],
    coalesce: ['---'],
    omitWithoutCoalesce: true,
    displayName: 'kt_aws_dst_vpc_name'
  },
  kt_aws_src_subnet_name: {
    columns: ['kt_aws_src_subnet_id'],
    coalesce: ['---'],
    omitWithoutCoalesce: true,
    displayName: 'kt_aws_src_subnet_name'
  },
  kt_aws_dst_subnet_name: {
    columns: ['kt_aws_dst_subnet_id'],
    coalesce: ['---'],
    omitWithoutCoalesce: true,
    displayName: 'kt_aws_dst_subnet_name'
  },

  // PeeringDB
  kt_src_pdb_asn_common_ix: {
    columns: ['kt_src_pdb_asn_common_ix'],
    coalesce: ['---'],
    displayName: 'kt_src_pdb_asn_common_ix'
  },
  kt_dst_pdb_asn_common_ix: {
    columns: ['kt_dst_pdb_asn_common_ix'],
    coalesce: ['---'],
    displayName: 'kt_dst_pdb_asn_common_ix'
  },
  kt_src_pdb_asn_common_fac: {
    columns: ['kt_src_pdb_asn_common_fac'],
    coalesce: ['---'],
    displayName: 'kt_src_pdb_asn_common_fac'
  },
  kt_dst_pdb_asn_common_fac: {
    columns: ['kt_dst_pdb_asn_common_fac'],
    coalesce: ['---'],
    displayName: 'kt_dst_pdb_asn_common_facs'
  },

  src_mapt_sub_id: {
    columns: ['src_mapt_sub_id'],
    convertHex: true,
    displayName: 'src_mapt_sub_id'
  },
  dst_mapt_sub_id: {
    columns: ['dst_mapt_sub_id'],
    convertHex: true,
    displayName: 'dst_mapt_sub_id'
  }
};
